// src/pages/AssessmentPage.js
//
// Route: /assessment
// Assessment view only. The hub lives at /intelligence.
// Navigates back to /intelligence on exit.
// Accepts ?generate=1 to auto-trigger generation on mount.

import React, { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useSubject } from '../context/SubjectContext'
import { createClient } from '@supabase/supabase-js'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

// ─── Display components ───────────────────────────────────────────────────────

function LoadingStep({ text, delay }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay * 1000)
    return () => clearTimeout(t)
  }, [delay])
  if (!visible) return null
  return (
    <div className="asmt-loading-step">
      <span className="asmt-loading-check">✓</span>
      {text}
    </div>
  )
}

function Accordion({ topic, detail }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="asmt-accordion-item">
      <button className="asmt-accordion-trigger" onClick={() => setOpen(o => !o)}>
        <div>
          <div className="u-label-sm">{topic}</div>
          {!open && <div className="u-hint">Technical detail available</div>}
        </div>
        <span className="asmt-accordion-toggle">{open ? "▲ Collapse" : "▼ Expand"}</span>
      </button>
      {open && <div className="asmt-accordion-body">{detail}</div>}
    </div>
  )
}

function ResponsibilityCard({ item }) {
  const confClass = { clear: "conf-clear", likely: "conf-likely", uncertain: "conf-uncertain" }[item.confidence] || "conf-likely"
  const confLabel = { clear: "High confidence", likely: "Likely", uncertain: "Needs confirmation" }[item.confidence] || "Likely"
  return (
    <div className={`u-card ${confClass}`}>
      <div className="asmt-resp-card-top">
        <div className="u-card-title">{item.item}</div>
        <div className="asmt-resp-conf">
          <span className="asmt-resp-conf-dot" />
          <span className="asmt-resp-conf-label">{confLabel}</span>
        </div>
      </div>
      <p className="asmt-resp-what">{item.what_it_is}</p>
      <p className="asmt-resp-why">{item.why_it_applies}</p>
      {item.confidence_reason && <p className="asmt-resp-reason">{item.confidence_reason}</p>}
      <div className="asmt-resp-tags">
        {item.deadline && <span className="asmt-tag-deadline">Due: {item.deadline}</span>}
        {item.professional_review && <span className="asmt-tag-review">Professional review recommended</span>}
      </div>
    </div>
  )
}

function CoordinationCard({ item }) {
  const sevClass = { low: "sev-low", medium: "sev-medium", high: "sev-high" }[item.severity] || "sev-medium"
  return (
    <div className={`asmt-coord-card ${sevClass}`}>
      <div className="asmt-coord-title">{item.title}</div>
      <p className="u-text-muted-sm">{item.explanation}</p>
      {item.why_it_matters && <p className="asmt-coord-matter">{item.why_it_matters}</p>}
      {item.action && <p className="asmt-coord-action">→ {item.action}</p>}
    </div>
  )
}

function ActivityIntersectionCard({ item }) {
  return (
    <div className="u-card">
      <div className="asmt-activity-title">{item.activity}</div>
      <div className="asmt-activity-columns">
        {item.spain_items?.length > 0 && (
          <div className="asmt-activity-col asmt-activity-col-es">
            <div className="asmt-activity-col-header"><span>🇪🇸</span><span className="asmt-activity-col-label-es">Spain</span></div>
            <ul className="asmt-activity-items">{item.spain_items.map((s, i) => <li key={i} className="asmt-activity-item">· {s}</li>)}</ul>
          </div>
        )}
        {item.us_items?.length > 0 && (
          <div className="asmt-activity-col asmt-activity-col-us">
            <div className="asmt-activity-col-header"><span>🇺🇸</span><span className="asmt-activity-col-label-us">United States</span></div>
            <ul className="asmt-activity-items">{item.us_items.map((u, i) => <li key={i} className="asmt-activity-item">· {u}</li>)}</ul>
          </div>
        )}
      </div>
      {item.coordination_point && (
        <div className="asmt-activity-coordination">
          <div className="asmt-activity-coordination-header">
            <span className="asmt-activity-coordination-icon">⚡</span>
            <span className="asmt-activity-coordination-label">Why this becomes complicated</span>
          </div>
          <p className="asmt-activity-coordination-text">{item.coordination_point}</p>
        </div>
      )}
    </div>
  )
}

function SectionLabel({ children }) { return <div className="asmt-section-label">{children}</div> }
function SectionIntro({ children }) { return <p className="asmt-section-intro">{children}</p> }

// ─── AssessmentPage ───────────────────────────────────────────────────────────

export default function AssessmentPage() {
  const { session }                      = useAuth()
  const { activeSubject, activeTaxYear } = useSubject()
  const navigate                         = useNavigate()
  const [searchParams]                   = useSearchParams()
  const printRef                         = useRef()
  const pollRef                          = useRef(null)
  const POLL_KEY                         = "assessment_poll_trigger"

  const [state,       setState]       = useState("checking")
  const [assessment,  setAssessment]  = useState(null)
  const [generatedAt, setGeneratedAt] = useState(null)
  const [errorMsg,    setErrorMsg]    = useState(null)

  const activeSubjectId = activeSubject?.id || null

  // ── Polling ───────────────────────────────────────────────────────────────
  function startPolling(triggerTime) {
    if (pollRef.current) clearInterval(pollRef.current)
    const start = Date.now()
    pollRef.current = setInterval(async () => {
      if (Date.now() - start > 180_000) {
        clearInterval(pollRef.current)
        sessionStorage.removeItem(POLL_KEY)
        setErrorMsg("Generation is taking longer than expected. Please try again.")
        setState("error")
        return
      }
      const query = supabase
        .from("assessments")
        .select("id, generated_at, content")
        .eq("tax_year", activeTaxYear)
        .order("generated_at", { ascending: false })
        .limit(1)
        .maybeSingle()
      if (activeSubject?.subject_type === "self") {
        query.or(`subject_id.eq.${activeSubjectId},subject_id.is.null`).eq("user_id", session.user.id)
      } else {
        query.eq("subject_id", activeSubjectId)
      }
      const { data } = await query
      if (data && data.generated_at > triggerTime) {
        clearInterval(pollRef.current)
        sessionStorage.removeItem(POLL_KEY)
        setAssessment(data.content)
        setGeneratedAt(data.generated_at)
        setState("viewing")
      }
    }, 3000)
  }

  // ── Generate ──────────────────────────────────────────────────────────────
  async function generate() {
    setState("loading")
    setErrorMsg(null)
    const triggerTime = new Date().toISOString()
    sessionStorage.setItem(POLL_KEY, triggerTime)
    fetch("https://rzyosihllycnqemexzdm.supabase.co/functions/v1/generate-assessment", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify({ subjectId: activeSubjectId, taxYear: activeTaxYear }),
      keepalive: true,
    }).then(res => {
      if (!res.ok) {
        res.json().catch(() => ({})).then(err => {
          console.error("generate-assessment error:", err)
          sessionStorage.removeItem(POLL_KEY)
          setErrorMsg(err?.error || "Generation failed. Please try again.")
          setState("error")
        })
      }
    }).catch(() => {})
    startPolling(triggerTime)
  }

  // ── Load existing / resume poll / auto-generate ───────────────────────────
  useEffect(() => {
    if (!session?.user || !activeSubjectId) return
    if (!assessment) setState("checking")

    const savedTrigger  = sessionStorage.getItem(POLL_KEY)
    const autoGenerate  = searchParams.get("generate") === "1"

    const query = supabase
      .from("assessments")
      .select("id, generated_at, content, subject_id")
      .order("generated_at", { ascending: false })
      .limit(1)
    if (activeTaxYear) query.eq("tax_year", activeTaxYear)
    if (activeSubject?.subject_type === "self") {
      query.or(`subject_id.eq.${activeSubjectId},subject_id.is.null`).eq("user_id", session.user.id)
    } else {
      query.eq("subject_id", activeSubjectId)
    }

    query.maybeSingle().then(({ data, error }) => {
      if (error) console.error("Assessment lookup error:", error)
      if (data) {
        if (savedTrigger && data.generated_at > savedTrigger) sessionStorage.removeItem(POLL_KEY)
        setAssessment(data.content)
        setGeneratedAt(data.generated_at)
        setState("viewing")
      } else if (savedTrigger) {
        setState("loading")
        startPolling(savedTrigger)
      } else if (autoGenerate) {
        generate()
      } else {
        setState("empty")
      }
    }).catch(err => {
      console.error("Assessment load error:", err)
      setState("empty")
    })

    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [session, activeSubjectId, activeSubject?.subject_type, activeTaxYear])

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : ""

  const subjectName = activeSubject?.subject_type !== "self" ? activeSubject?.display_name : null

  // ── Render ────────────────────────────────────────────────────────────────

  if (state === "checking") {
    return <div className="asmt-state"><div className="app-spinner" /></div>
  }

  if (state === "empty") {
    return (
      <div className="asmt-state">
        <h2 className="asmt-state-h2">No assessment yet</h2>
        <p className="asmt-state-sub">Generate your full US × Spain assessment to see a detailed dual-jurisdiction brief.</p>
        <button className="btn-primary" onClick={generate}>Generate Full Assessment →</button>
        <p className="u-meta-note" style={{ marginTop: "0.75rem", fontSize: "var(--text-xs)", color: "var(--faint)" }}>
          Takes ~60–90 seconds
        </p>
      </div>
    )
  }

  if (state === "loading") {
    return (
      <div className="asmt-state">
        <div className="app-spinner" style={{ width: 36, height: 36, marginBottom: "1.5rem" }} />
        <h2 className="asmt-state-h2" style={{ fontSize: "var(--text-2xl)" }}>Analysing your situation…</h2>
        <p className="asmt-state-sub" style={{ marginBottom: "2rem" }}>This takes about 30–45 seconds.</p>
        <div className="asmt-loading-steps">
          {[
            ["Reading residency and citizenship status",         0  ],
            ["Mapping US filing obligations",                    1.5],
            ["Mapping Spanish IRPF and reporting requirements",  3  ],
            ["Assessing treaty interactions",                    4.5],
            ["Identifying coordination areas",                   6  ],
            ["Drafting next steps",                              7.5],
          ].map(([text, delay]) => (
            <LoadingStep key={text} text={text} delay={delay} />
          ))}
        </div>
      </div>
    )
  }

  if (state === "error") {
    return (
      <div className="asmt-state">
        <h2 className="asmt-state-h2" style={{ fontSize: "var(--text-2xl)" }}>Something went wrong</h2>
        <p className="asmt-state-sub">{errorMsg || "The assessment could not be generated. Please try again."}</p>
        <button className="btn-primary" onClick={generate}>Try again →</button>
      </div>
    )
  }

  if (state === "viewing" && assessment) {
    const a = assessment

    const complexityClass = { simple: "pill-teal", moderate: "pill-amber", complex: "pill-red" }[a.situation_summary?.complexity_band] || "pill-amber"
    const complexityLabel = { simple: "Relatively straightforward", moderate: "Meaningful cross-border considerations", complex: "Multiple high-complexity factors" }[a.situation_summary?.complexity_band] || "Meaningful cross-border considerations"
    const complexityIcon  = { simple: "○", moderate: "◑", complex: "●" }[a.situation_summary?.complexity_band] || "◑"

    return (
      <div ref={printRef} className="asmt-page">

        {/* ── Back ── */}
        <div className="asmt-back-row no-print">
          <button className="asmt-back-btn" onClick={() => navigate("/intelligence")}>
            ← Back to intelligence hub
          </button>
        </div>

        {/* ── Header ── */}
        <div className="asmt-header">
          <div>
            <div className="asmt-header-eyebrow">
              Situation Assessment{subjectName ? ` · ${subjectName}` : ""}
            </div>
            <h1 className="asmt-header-title">
              {a.situation_summary?.headline || "US–Spain Tax Brief"}
            </h1>
            {generatedAt && (
              <p className="u-meta-note">
                Generated {formatDate(generatedAt)} · Not legal or tax advice
              </p>
            )}
          </div>
          <div className="asmt-header-actions">
            <button className="asmt-action-btn" onClick={() => window.print()}>Print</button>
            <button className="asmt-action-btn" onClick={generate}>Regenerate</button>
          </div>
        </div>

        <div className="u-stack-lg">

          {/* 1. Situation Summary */}
          {a.situation_summary && (
            <div className="asmt-section">
              <SectionLabel>Overview</SectionLabel>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--muted)", marginBottom: "0.5rem" }}>
                {a.situation_summary.profile_line}
              </div>
              <p style={{ fontSize: "var(--text-base)", lineHeight: 1.7, color: "var(--ink)", margin: "1.25rem 0" }}>
                {a.situation_summary.orientation}
              </p>
              {a.situation_summary.reassurance && (
                <p style={{ fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--muted)", fontStyle: "italic" }}>
                  {a.situation_summary.reassurance}
                </p>
              )}
              <div className={`asmt-complexity-badge ${complexityClass}`}>
                {complexityIcon} {complexityLabel}
              </div>
            </div>
          )}

          {/* 2. Complexity Drivers */}
          {a.complexity_drivers?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>What's creating cross-border complexity</SectionLabel>
              <SectionIntro>These factors explain why your situation spans two tax systems.</SectionIntro>
              <div className="asmt-driver-list">
                {a.complexity_drivers.map((d, i) => (
                  <div key={i} className="asmt-driver">
                    <span className="asmt-driver-icon">{d.icon}</span>
                    <div>
                      <div className="asmt-driver-title">{d.driver}</div>
                      <div className="u-text-muted-sm">{d.explanation}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Activity Intersections */}
          {a.activity_intersections?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>How your financial life gets seen twice</SectionLabel>
              <SectionIntro>Each of your financial activities is interpreted simultaneously by both tax systems.</SectionIntro>
              <div className="asmt-activity-list">
                {a.activity_intersections.map((item, i) => (
                  <ActivityIntersectionCard key={i} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* 4. Spain calculators */}
          <div className="asmt-section">
            <SectionLabel>🧮 Spain tax calculators</SectionLabel>
            <SectionIntro>Detailed estimates for your Spain-side obligations.</SectionIntro>
            <div className="asmt-calc-cards">
              <Link to="/calculator" className="asmt-calc-card asmt-calc-card--available">
                <div className="asmt-calc-card-icon">🇪🇸</div>
                <div>
                  <div className="u-card-title-sm">IRPF Calculator</div>
                  <div className="u-text-muted-xs">Estimate your Spanish income tax liability step by step.</div>
                </div>
                <span className="asmt-calc-card-arrow">→</span>
              </Link>
              <div className="asmt-calc-card asmt-calc-card--soon">
                <div className="asmt-calc-card-icon">💰</div>
                <div>
                  <div className="u-card-title-sm">Wealth Tax Calculator</div>
                  <div className="u-text-muted-xs">Patrimonio and ITSGF exposure by Comunidad Autónoma.</div>
                </div>
                <span className="asmt-calc-card-coming">Coming soon</span>
              </div>
            </div>
          </div>

          {/* 5. Timeline */}
          {a.timeline?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>Your timeline</SectionLabel>
              <SectionIntro>Key moments that shape your cross-border obligations.</SectionIntro>
              <div>
                {a.timeline.map((item, i) => (
                  <div key={i} className="asmt-timeline-item">
                    <div className="asmt-timeline-date">{item.date}</div>
                    <div className={`asmt-timeline-event${item.inferred ? " inferred" : ""}`}>
                      {item.event}
                      {item.inferred && <span className="asmt-timeline-estimated">(estimated)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 6. Spain Responsibilities */}
          {a.spain_responsibilities?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>Things to handle in Spain</SectionLabel>
              <div className="asmt-jurisdiction-pill-es">🇪🇸 Spain</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {a.spain_responsibilities.map((item, i) => <ResponsibilityCard key={i} item={item} />)}
              </div>
            </div>
          )}

          {/* 7. US Responsibilities */}
          {a.us_responsibilities?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>Things to handle in the US</SectionLabel>
              <div className="asmt-jurisdiction-pill-us">🇺🇸 United States</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {a.us_responsibilities.map((item, i) => <ResponsibilityCard key={i} item={item} />)}
              </div>
            </div>
          )}

          {/* 8. Coordination Areas */}
          {a.coordination_areas?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>Areas worth reviewing carefully</SectionLabel>
              <SectionIntro>These are coordination points — not problems — that deserve careful attention.</SectionIntro>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {[...a.coordination_areas]
                  .sort((a, b) => ["high","medium","low"].indexOf(a.severity) - ["high","medium","low"].indexOf(b.severity))
                  .map((item, i) => <CoordinationCard key={i} item={item} />)
                }
              </div>
            </div>
          )}

          {/* 9. Missing Information */}
          {a.missing_information?.length > 0 && (
            <div className="asmt-section-muted">
              <SectionLabel>Additional information that would improve precision</SectionLabel>
              <div>
                {a.missing_information.map((item, i) => (
                  <div key={i} className="asmt-missing-item">
                    <span className="asmt-missing-bullet">◦</span>
                    <div>
                      <div className="asmt-missing-field">{item.field}</div>
                      <div className="asmt-missing-why">{item.why_it_matters}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 10. Next Steps */}
          {a.next_steps && (
            <div className="asmt-section">
              <SectionLabel>Recommended next steps</SectionLabel>
              <div style={{ marginTop: "0.5rem" }}>
                {[
                  { key: "immediate",     label: "Immediate" },
                  { key: "before_filing", label: "Before filing" },
                  { key: "advisor_prep",  label: "Advisor preparation" },
                  { key: "ongoing",       label: "Ongoing" },
                ].map(({ key, label }) => {
                  const items = a.next_steps[key]
                  if (!items?.length) return null
                  return (
                    <div key={key} className="asmt-next-steps-group">
                      <div className="asmt-next-steps-label">{label}</div>
                      {items.map((item, i) => (
                        <div key={i} className="asmt-next-step">
                          <span className="asmt-next-step-arrow">→</span>
                          <div>
                            <div className="u-item-title">{item.step}</div>
                            <div className="u-item-note">{item.rationale}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* 11. Technical Appendix */}
          {a.technical_appendix?.length > 0 && (
            <div className="asmt-section">
              <SectionLabel>Technical detail</SectionLabel>
              <SectionIntro>Expand any topic for in-depth technical treatment.</SectionIntro>
              <div>
                {a.technical_appendix.map((item, i) => (
                  <Accordion key={i} topic={item.topic} detail={item.detail} />
                ))}
              </div>
            </div>
          )}

          {a.disclaimer && <div className="asmt-disclaimer">{a.disclaimer}</div>}

        </div>
      </div>
    )
  }

  return null
}